import { inject, Inject, Injectable, PLATFORM_ID, Renderer2 } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { PhotoSwipOptions } from './entity/PhotoSwipOptions';
import { PhotoSwipeSlide, PhotoSwipeFigureSlide } from './entity/PhotoSwipeSlide';
import { PhotoSwipeFigureSlideDownLoadData } from './entity/PhotoSwipeFigureSlideDownLoadData';
import { TYPE1_OPTIONS } from './entity/Authority';
import { isPlatformBrowser } from '@angular/common';
import { checkIsTouchMobile } from '@techmore/global';
import { find } from './util';
declare let PhotoSwipeUI_TM: any;
declare let PhotoSwipe: any;

/** 可手勢滑動的相簿瀏覽器服務 */
@Injectable()
export class PhotoSwipeService {
  protected renderer: Renderer2 = inject(Renderer2);
  /** 圖片資料 */
  data$ = new BehaviorSubject<any[]>([]);
  get data() { return this.data$.value; }
  set data(d: any[]) { this.data$.next(d); }
  /** PhotoSwipe 實例 */
  protected photoSwiper: any;
  /** 相簿瀏覽器是否開啟中 */
  isOpened$ = new BehaviorSubject<boolean>(false);
  /** 相簿瀏覽器是否開啟中 */
  get isOpened() { return this.isOpened$.value; }
  /** 相簿瀏覽器是否開啟中 */
  set isOpened(open: boolean) { this.isOpened$.next(open); }
  /** 檢查圖片是否載入用 */
  protected changes?: MutationObserver;
  /** 當前瀏覽Slide的指標改變 */
  indexChanged$ = new Subject<number>();
  /** 當前瀏覽Slide的指標 */
  get currentIndex() { return this.photoSwiper && this.photoSwiper.getCurrentIndex(); }
  /** 當前瀏覽Slide */
  get currentItem() { return this.data[this.currentIndex] as PhotoSwipeSlide; }
  /** 圖片下載資料 */
  downloadData$ = new BehaviorSubject<PhotoSwipeFigureSlideDownLoadData|undefined>(undefined);
  /** 圖片下載資料 */
  get downloadData() { return this.downloadData$.value; }
  /** 圖片下載資料 */
  set downloadData(d: PhotoSwipeFigureSlideDownLoadData|undefined)  { this.downloadData$.next(d); }
  get isCurrentHtmlSlide() {
    if ((this.data[this.currentIndex] as any).src) {
      return false;
    } else {
      return true;
    }
  }
  get isCurrentFigureSlide() {
    return !this.isCurrentHtmlSlide;
  }
  get canDownload() {
    const slide = this.currentItem as PhotoSwipeFigureSlide;
    return this.isCurrentFigureSlide && slide.imageAuthority && slide.imageAuthority.type !== 11 && slide.imageAuthority.type !== 12;
  }
  isTouchDevice = false;
  get showthumbnailBar() {
    return this.photoSwiper && this.photoSwiper.options.thumbnailBar;
  }

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
  ) {
    if (isPlatformBrowser(this.platformId)) {
      this.changes = new MutationObserver((mutations: MutationRecord[]) => {
        this.cancelObserveDomChange();
        this.checkIsImgLoaded();
        this.startObserveDomChange(this.photoSwiper.currItem.container.parentElement);
      });
      this.isTouchDevice = checkIsTouchMobile();
    }
  }

  /** 開啟相簿瀏覽器 */
  open(openIndex: string, slides: PhotoSwipeFigureSlide[], galleryEl: HTMLElement, options?: PhotoSwipOptions, mapCurrentItemByDataItem = false) {
    // 相簿瀏覽器 dom
    const pswpElement = document.querySelectorAll('.pswp')[0] as HTMLElement;

    // 剖析出相片物件陣列
    this.parseThumbnailElements(slides, galleryEl, mapCurrentItemByDataItem);
    this.data = slides;

    // 更新選項
    // TODO: 加入如果是html 的狀況 https://photoswipe.com/documentation/custom-html-in-slides.html
    options = Object.assign({
      tapToToggleControls: false,
      closeOnScroll: false,
      // define gallery index (for URL)
      // galleryUID: galleryEl.getAttribute('data-pswp-uid'),
      getThumbBoundsFn: (index: number) => {
        // See Options -> getThumbBoundsFn section of documentation for more info
        const pageYScroll = window.pageYOffset || document.documentElement.scrollTop;
        if ((slides[index] as any).src) {
          const slide = slides[index] as PhotoSwipeFigureSlide;
          const thumbnail = slide?.el?.getElementsByTagName('img')[0]; // find thumbnail
          const rect = thumbnail?.getBoundingClientRect();

          return {x: rect!.left, y: rect!.top + pageYScroll, w: rect!.width};
        }
        return null;
      },
      addCaptionHTMLFn: (item: any, captionEl: any, isFake: any) => {
        let authHtml = '';
        if (item.imageAuthorityImg) {
          authHtml = `<a href="${ item.imageAuthorityUrl }" target="_blank" class="copyright-link" title="${ item.imageAuthorityText }[另開新視窗]">
            <img src="${ item.imageAuthorityImg }" alt="${ item.imageAuthorityText }" style="height:20px;width:auto;">
          </a>`;
        } else if (item.imageAuthorityUrl) {
          authHtml = `<a href="${ item.imageAuthorityUrl }" target="_blank" class="copyright-link" title="${ item.imageAuthorityText }[另開新視窗]">
            ${ item.imageAuthorityText }
          </a>`;
        } else if (item.imageAuthorityText) {
          authHtml = item.imageAuthorityText;
        }

        const unsafeHtml = `<div class="caption_tile">${item.title}</div>`
        + (item.desc === undefined ? '' : `<span class="caption">${item.desc}</span>`)
        + (item.imageAuthMan && authHtml ? `<p>授權者：${item.imageAuthMan}&nbsp;&nbsp;&nbsp;${authHtml}</p>` : '')
        + (item.imageAuthMan && !authHtml ? `<p>授權者：${item.imageAuthMan}</p>` : '')
        + (!item.imageAuthMan && authHtml ? `<p>${authHtml}</p>` : '');
        this.renderer.setProperty(captionEl.children[0], 'innerHTML', unsafeHtml);

        return true;
      }
    }, options);
    options.index = parseInt(openIndex, 10);

    // 初始化相簿瀏覽器
    this.photoSwiper = new PhotoSwipe(pswpElement, PhotoSwipeUI_TM, slides, options);
    this.photoSwiper.init();
    pswpElement.focus();
    this.isOpened = true;
    this.startObserveDomChange(this.photoSwiper.currItem.container.parentElement);
    this.photoSwiper.items.forEach((item: PhotoSwipeFigureSlide) => {
      if (item.img && item.alt) {
        item.img.alt = item.alt;
      }
    });
    this.indexChanged$.next(options.index);

    this.photoSwiper.listen('close', () => {
      pswpElement.blur();
      this.isOpened = false;
      this.cancelObserveDomChange();
      this.clearDownloadBoxData();
    });

    this.photoSwiper.listen('beforeChange', () => {
      this.checkIsImgLoaded();
    });

    this.photoSwiper.listen('afterChange', () => {
      this.checkIsImgLoaded();
    });

    this.photoSwiper.listen('preventDragEvent', (e: any, isDown: boolean, preventObj: any) => {
      // e - original event
      // isDown - true = drag start, false = drag release

      // Line below will force e.preventDefault() on:
      // touchstart/mousedown/pointerdown events
      // as well as on:
      // touchend/mouseup/pointerup events
      preventObj.prevent = false;
    });

    this.photoSwiper.listen('pointerDown', (e: any) => {
      if (!!find(pswpElement.querySelector('.pswp__bottom-bar') as HTMLElement, e.target)) {
        // to stop slide
        throw new Error('');
      }
    });

    this.photoSwiper.listen('downloadModalOpen', () => {
      this.setDownloadBoxData();
    });

    this.photoSwiper.listen('downloadModalClose', () => {
      this.clearDownloadBoxData();
    });

    document.body.addEventListener('focusin', this.handleFocusInEventHandler);
  }

  viewIn(index: number) {
    if (!this.photoSwiper) {
      console.error('code error: call open first');
      return;
    }
    this.photoSwiper.goTo(index);
  }

  /** 關閉相簿瀏覽器 */
  close() {
    this.photoSwiper.close();
    this.photoSwiper = undefined;
    this.isOpened = false;
    document.body.removeEventListener('focusin', this.handleFocusInEventHandler);
  }

  /** 打開圖片下載提示窗 */
  protected setDownloadBoxData() {
    const currentItem = this.currentItem as PhotoSwipeFigureSlide;
    this.downloadData = currentItem;
  }

  /** 關閉圖片下載提示窗 */
  protected clearDownloadBoxData() {
    this.downloadData = undefined;
  }

  /** 查看當前焦點是否在相簿瀏覽器內，若不在就關閉相簿瀏覽器 */
  protected handleFocusInEventHandler = (event: any) => {
    if (this.isOpened) {
      const tg = event.target as HTMLElement;
      const pswp = document.querySelector('.pswp');
      if (pswp !== tg) {
        const find = tg.closest('.pswp');
        if (!find) { this.close(); }
      }
    }
  }

  /** 檢查子節點是否改變 */
  protected startObserveDomChange(observeDom: HTMLElement) {
    this.changes?.observe(observeDom, {
      childList: true,
      subtree: true,
    });
  }

  /** 取消檢查子節點是否改變 */
  protected cancelObserveDomChange() {
    this.changes?.disconnect();
  }

  /** 檢查圖片是否已經載入，載入好就改變他的 alt 成圖案名稱 */
  protected checkIsImgLoaded() {
    const currentIndex = this.photoSwiper.getCurrentIndex();
    let lastIndex = currentIndex - 1 < 0 ? this.photoSwiper.items.length - 1 : currentIndex - 1;
    let nextIndex = currentIndex + 1 === this.photoSwiper.items.length ? 0 : currentIndex + 1;

    const currItem = this.photoSwiper.currItem as PhotoSwipeFigureSlide;
    if ((currItem as any)['imageAppended'] && (currItem as any)['container']) {
      const img = ((currItem as any)['container'] as HTMLElement).querySelector('img.pswp__img:not(.pswp__img--placeholder)') as HTMLImageElement;
      img.alt = currItem.alt as string;
    }

    const lastItem = this.photoSwiper.items[lastIndex] as PhotoSwipeFigureSlide;
    if ((lastItem as any)['imageAppended'] && (lastItem as any)['container']) {
      const img = ((lastItem as any)['container'] as HTMLElement).querySelector('img.pswp__img:not(.pswp__img--placeholder)') as HTMLImageElement;
      img.alt = lastItem.alt as string;
    }

    const nextItem = this.photoSwiper.items[nextIndex] as PhotoSwipeFigureSlide;
    if ((nextItem as any)['imageAppended'] && (nextItem as any)['container']) {
      const img = ((nextItem as any)['container'] as HTMLElement).querySelector('img.pswp__img:not(.pswp__img--placeholder)') as HTMLImageElement;
      img.alt = nextItem.alt as string;
    }
  }

  // parse slide data (url, title, size ...) from DOM elements
  // (children of gallerySelector)
  /**
   * @param {boolean} [mapCurrentItemByDataItem=false] 是否藉由 figure 的 attr.data-item 去決定 對應的 PhotoSwipeFigureSlide, 預設否 (用 owl輪播的loop, 需開啟)
   */
  private parseThumbnailElements(slides: PhotoSwipeFigureSlide[], galleryEl: HTMLElement, mapCurrentItemByDataItem = false) {
    const thumbElements = galleryEl.children;
    let figureEl: HTMLElement;
    let img: HTMLImageElement|null;

    // tslint:disable-next-line: prefer-for-of
    for (let i = 0, slideIndex = 0; i < thumbElements.length; i++) {
      if ((thumbElements[i] as any).children) {
        figureEl = (thumbElements[i] as HTMLElement).children[0] as HTMLElement;  // <figure> element
      } else { figureEl = thumbElements[i] as HTMLElement; }

      // include only element nodes
      if (figureEl.nodeType !== 1) {
        continue;
      }
      img = figureEl.querySelector('img[hidden]'); // getAttribute('data-size').split('x');
      if (mapCurrentItemByDataItem) {
        try {
          if (figureEl.tagName === 'FIGURE') {
            slideIndex = Number($(figureEl).attr('data-item'));
          } else {
            slideIndex = Number($(figureEl).find('figure').attr('data-item'));
          }
        } catch (error) {
          slideIndex = i;
        }
      }
      const currentItem = slides[slideIndex];
      // create slide object
      Object.assign(currentItem, {
        w: img!.width,
        h: img!.height,
        el: figureEl, // save link to element for getThumbBoundsFn
        fileExt: currentItem.src.substr(currentItem.src.lastIndexOf('.') + 1, currentItem.src.length).toUpperCase(),
      });
      if (currentItem.imageAuthority) {
        const type1Option = TYPE1_OPTIONS.find(item => item.value === currentItem.imageAuthority!.type);
        if (type1Option) {
          currentItem.imageAuthorityText = type1Option.label;
          currentItem.imageAuthorityImg = type1Option.image;
          currentItem.imageAuthorityUrl = type1Option.url;
        }
      } else {
        currentItem.imageAuthorityText = '';
      }
      if (!currentItem.originUrl) {
        currentItem.originUrl = window.location.href;
      }
      slideIndex++;
    }
  }
}
